import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import { THERAPY_DISCIPLINE_OPTIONS } from 'src/consumer/constants'
import { APPROVAL_DISAPPROVAL_DIALOG_TYPES } from 'src/supportPortal/views/serviceApprovals/hooks/useApprovalDialog'

export default function DisapprovalApprovalDialog({
  open,
  onClose,
  onSubmit,
  approvalDisapprovalRole,
  approvalActionType,
  practitioner,
  specializations,
  loading,
}) {

  const practitionerName = practitioner?.name


  const approvalLabel = approvalDisapprovalRole?.title
  let dialogTitle = ''
  let dialogActionButtonTitle = ''
  switch (approvalActionType) {
    case APPROVAL_DISAPPROVAL_DIALOG_TYPES.APPROVE:
      dialogTitle = `Approve ${practitionerName} as ${approvalLabel}`
      dialogActionButtonTitle = 'Approve as ' + approvalLabel + ' role'
      break
    case APPROVAL_DISAPPROVAL_DIALOG_TYPES.DISAPPROVE:
      dialogTitle = `Disapprove ${practitionerName} for ${approvalLabel}`
      dialogActionButtonTitle = 'Disapprove for ' + approvalLabel + ' role'
      break
    default:
      dialogTitle += 'Action not supported contact developer'
      dialogActionButtonTitle = 'Action not supported contact developer'
  }

  const [remarks, setRemarks] = useState('')
  const [selectedSpecializations, setSelectedSpecializations] = useState([])
  // KYC-locked therapy disciplines this practitioner is approved for. Surfaced only on the
  // ROLE_THERAPIST approval path; the backend ignores it for ROLE_DOCTOR / ROLE_SCREENER.
  const [selectedDisciplines, setSelectedDisciplines] = useState([])

  const isTherapistApproval = approvalDisapprovalRole?.id === 'ROLE_THERAPIST'

  const handleSubmit = () => {
    if (!remarks.trim()) {
      alert('Please enter remarks')
      return
    }
    const specializationIds = selectedSpecializations.map((spec) => spec)
    const disciplinesToSend = isTherapistApproval
      && approvalActionType === APPROVAL_DISAPPROVAL_DIALOG_TYPES.APPROVE
      ? selectedDisciplines.map((d) => d?.code ?? d)
      : null
    onSubmit(remarks, specializationIds, disciplinesToSend)
  }

  function resetForm(){
    setRemarks('')
    setSelectedSpecializations([])
    setSelectedDisciplines([])
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const getFilteredSpecializations = () => {
    if (!approvalDisapprovalRole) return []

    // Filter based on approval type
    const categoryTypeMap = {
      ROLE_DOCTOR: 'doctor',
      ROLE_THERAPIST: 'therapist',
      ROLE_SCREENER: 'therapist', // Screeners use therapist specializations
    }

    const categoryType = categoryTypeMap[approvalDisapprovalRole?.id || '']
    return (
      specializations
        ?.filter((option) => option.categoryType === categoryType)
        .sort((a, b) => a.title.localeCompare(b.title)) || []
    )
  }

  useEffect(() => {
    if (open) {
      if (
        approvalActionType === APPROVAL_DISAPPROVAL_DIALOG_TYPES.APPROVE &&
        practitioner?.teacherUnEditableFields?.servicesApproved?.[approvalDisapprovalRole?.id]
      ) {
        setSelectedSpecializations(
          (practitioner?.teacherUnEditableFields?.servicesApproved?.[approvalDisapprovalRole?.id]?.specializations || []),
        )
      }
      // Pre-fill therapy disciplines from the practitioner's existing KYC value (or
      // [psychotherapy] when nothing is set, mirroring the backend default).
      if (
        approvalActionType === APPROVAL_DISAPPROVAL_DIALOG_TYPES.APPROVE
        && approvalDisapprovalRole?.id === 'ROLE_THERAPIST'
      ) {
        const existing = practitioner?.teacherUnEditableFields?.approvedTherapyDisciplines
        const codes = Array.isArray(existing) && existing.length > 0
          ? existing
          : [THERAPY_DISCIPLINE_OPTIONS[0].code]
        setSelectedDisciplines(
          codes.map((code) => THERAPY_DISCIPLINE_OPTIONS.find((opt) => opt.code === code) || { code, label: code }),
        )
      }
    } else {
      resetForm()
    }
  }, [open, approvalActionType, practitioner, approvalDisapprovalRole])


  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Practitioner: <strong>{practitionerName}</strong>
          </Typography>

          <TextField
            label="Remarks"
            multiline
            rows={4}
            required
            fullWidth
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            disabled={loading}
          />

          {approvalActionType === APPROVAL_DISAPPROVAL_DIALOG_TYPES.APPROVE && (
            <CustomSelect
              isMulti
              options={getFilteredSpecializations()}
              value={selectedSpecializations}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.name}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              selectType={selectTypes.string}
              disabled={loading}
              label="Specializations"
              menuPlacement="auto"
              onChangeEvent={(event) => {
                setSelectedSpecializations(event.target.value)
              }}
            />
          )}

          {/* Therapy disciplines — locked at KYC. Surfaced only on the therapist approval path
              (ignored backend-side for doctor / screener). */}
          {approvalActionType === APPROVAL_DISAPPROVAL_DIALOG_TYPES.APPROVE && isTherapistApproval && (
            <CustomSelect
              isMulti
              options={THERAPY_DISCIPLINE_OPTIONS}
              value={selectedDisciplines}
              getOptionValue={(option) => option.code}
              getOptionLabel={(option) => option.label}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              selectType={selectTypes.string}
              disabled={loading}
              label="Therapy disciplines (KYC-locked)"
              menuPlacement="auto"
              onChangeEvent={(event) => {
                setSelectedDisciplines(event.target.value)
              }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || !remarks.trim()}>
          {loading ? <CircularProgress size={20} /> : dialogActionButtonTitle}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
